<!-- Dashboard Browser Component -->
<div class="dashboard-browser" [class.loading]="IsLoading">
  <!-- Toolbar Row: Breadcrumb + Actions -->
  <div class="browser-toolbar">
    <!-- Breadcrumb Navigation -->
    <mj-dashboard-breadcrumb
      [Categories]="Categories"
      [CurrentCategoryId]="SelectedCategoryId"
      [RootIcon]="IconClass"
      [RootLabel]="Title"
      [AllowDragDrop]="AllowDragDrop && (DraggingId !== null || IsSelectionMode)"
      Size="large"
      (Navigate)="NavigateToCategory($event.CategoryId)"
      (DashboardDrop)="OnBreadcrumbDrop($event)">
    </mj-dashboard-breadcrumb>

    <!-- Right-side Actions -->
    <div class="toolbar-actions">
      <!-- Dashboard count -->
      <span class="dashboard-count">{{ FilteredDashboards.length }} dashboards</span>

      <!-- View Toggle -->
      <div class="view-toggle">
        <button
          class="toggle-btn"
          [class.active]="ViewMode === 'cards'"
          (click)="SetViewMode('cards')"
          title="Card View">
          <i class="fa-solid fa-grip"></i>
        </button>
        <button
          class="toggle-btn"
          [class.active]="ViewMode === 'list'"
          (click)="SetViewMode('list')"
          title="List View">
          <i class="fa-solid fa-list"></i>
        </button>
      </div>

      <!-- Selection Mode Toggle & Bulk Actions -->
      @if (AllowMultiSelect) {
        <!-- Enter Selection Mode Button (when not in selection mode) -->
        @if (!IsSelectionMode && FilteredDashboards.length > 0) {
          <button
            class="select-mode-btn"
            (click)="EnterSelectionMode()"
            title="Select multiple dashboards">
            <i class="fa-solid fa-check-double"></i>
            Select
          </button>
        }
        <!-- Selection Mode Toolbar (when in selection mode) -->
        @if (IsSelectionMode) {
          <div class="selection-toolbar">
            <span class="selected-count">{{ SelectedCount }} selected</span>
            <button class="action-btn" (click)="SelectAll()" title="Select all">
              <i class="fa-solid fa-check-double"></i>
            </button>
            <button class="action-btn" (click)="OnMoveSelected()" title="Move to folder" [disabled]="SelectedCount === 0">
              <i class="fa-solid fa-folder-open"></i>
            </button>
            <button class="action-btn danger" (click)="OnDeleteSelected()" title="Delete selected" [disabled]="!CanDeleteAnySelected">
              <i class="fa-solid fa-trash"></i>
            </button>
            <button class="action-btn" (click)="ExitSelectionMode()" title="Exit selection mode">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
        }
      }

      <!-- New Dropdown Button -->
      @if (ShowCreateButton) {
        <div class="new-dropdown">
          <button
            class="btn-primary"
            (click)="ToggleNewMenu(); $event.stopPropagation()">
            <i class="fa-solid fa-plus"></i>
            New
            <i class="fa-solid fa-caret-down dropdown-caret"></i>
          </button>
          @if (ShowNewMenu) {
            <div class="dropdown-menu">
              <button class="dropdown-item" (click)="OnCreateDashboard()">
                <i class="fa-solid fa-chart-line"></i>
                Dashboard
              </button>
              <button class="dropdown-item" (click)="OpenCreateCategoryDialog()">
                <i class="fa-solid fa-folder-plus"></i>
                Category
              </button>
            </div>
          }
        </div>
      }
    </div>
  </div>

  <!-- Click-away overlay for dropdown -->
  @if (ShowNewMenu) {
    <div class="dropdown-backdrop" (click)="CloseNewMenu()"></div>
  }

  <!-- Filters -->
  <div class="browser-filters">
    <div class="search-box">
      <i class="fa-solid fa-search"></i>
      <input
        type="text"
        [(ngModel)]="SearchText"
        (input)="OnSearchChange()"
        placeholder="Search dashboards...">
      @if (SearchText) {
        <button
          class="clear-btn"
          (click)="ClearSearch()">
          <i class="fa-solid fa-times"></i>
        </button>
      }
    </div>

    @if (SearchText) {
      <button
        class="clear-filters-btn"
        (click)="ClearSearch()">
        <i class="fa-solid fa-filter-circle-xmark"></i>
        Clear Search
      </button>
    }
  </div>

  <!-- Loading Overlay -->
  @if (IsLoading) {
    <div class="loading-overlay">
      <mj-loading text="Loading dashboards..."></mj-loading>
    </div>
  }

  <!-- Card View -->
  @if (ViewMode === 'cards' && (FilteredChildCategories.length > 0 || FilteredDashboards.length > 0)) {
    <div class="dashboard-grid">
      <!-- Category Folders -->
      @for (category of FilteredChildCategories; track TrackByCategory($index, category)) {
        <div
          class="dashboard-card category-card"
          [class.drop-target]="DragOverChildCategoryId === category.ID"
          (click)="NavigateToCategory(category.ID)"
          (dblclick)="$event.stopPropagation()"
          (dragover)="OnDragOverChildCategory(category.ID, $event)"
          (dragleave)="OnDragLeaveChildCategory()"
          (drop)="OnDropOnChildCategory(category.ID, $event)">
          <div class="card-header">
            <div class="card-icon folder-icon">
              <i class="fa-solid fa-folder"></i>
            </div>
            <div class="card-actions">
              <button
                class="action-btn danger"
                title="Delete category"
                (click)="OnDeleteCategory(category, $event)">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title" [innerHTML]="HighlightMatch(category.Name)"></h3>
            @if (category.Description) {
              <p class="card-description" [innerHTML]="HighlightMatch(category.Description)">
              </p>
            }
            <div class="card-meta">
              <span class="meta-item">
                <i class="fa-solid fa-folder-tree"></i>
                Category
              </span>
            </div>
          </div>
        </div>
      }
      <!-- Dashboard Cards -->
      @for (dashboard of FilteredDashboards; track TrackByDashboard($index, dashboard)) {
        <div
          class="dashboard-card"
          [class.selected]="IsSelected(dashboard.ID)"
          [class.dragging]="DraggingId === dashboard.ID"
          [draggable]="AllowDragDrop"
          (click)="OnDashboardClick(dashboard, $event)"
          (dblclick)="OnDashboardDoubleClick(dashboard, $event)"
          (dragstart)="OnDragStart(dashboard, $event)"
          (dragend)="OnDragEnd()">
          <!-- Selection checkbox in top-right corner -->
          @if (IsSelectionMode) {
            <div
              class="card-select-area"
              (click)="ToggleSelection(dashboard.ID); $event.stopPropagation()">
              <input
                type="checkbox"
                [checked]="IsSelected(dashboard.ID)"
                (change)="$event.stopPropagation()">
            </div>
          }
          <div class="card-header">
            <div class="card-icon">
              <i class="fa-solid fa-chart-line"></i>
            </div>
            <div class="card-actions">
              @if (CanEdit(dashboard.ID)) {
                <button
                  class="action-btn"
                  title="Edit"
                  (click)="OnEditDashboard(dashboard, $event)">
                  <i class="fa-solid fa-edit"></i>
                </button>
              }
              @if (CanDelete(dashboard.ID)) {
                <button
                  class="action-btn danger"
                  title="Delete"
                  (click)="OnDeleteDashboard(dashboard, $event)">
                  <i class="fa-solid fa-trash"></i>
                </button>
              }
            </div>
          </div>
          <div class="card-content">
            <h3 class="card-title">
              <span [innerHTML]="HighlightMatch(dashboard.Name)"></span>
              @if (IsShared(dashboard.ID)) {
                <span class="shared-badge" title="Shared with you">
                  <i class="fa-solid fa-share-nodes"></i>
                </span>
              }
            </h3>
            @if (dashboard.Description) {
              <p class="card-description" [innerHTML]="HighlightMatch(dashboard.Description)">
              </p>
            }
            <div class="card-meta">
              <span class="meta-item">
                <i class="fa-solid fa-clock"></i>
                {{ FormatDate(dashboard.__mj_UpdatedAt) }}
              </span>
            </div>
          </div>
        </div>
      }
    </div>
  }

  <!-- List View -->
  @if (ViewMode === 'list' && (FilteredChildCategories.length > 0 || FilteredDashboards.length > 0)) {
    <div class="dashboard-list">
      <div class="list-header">
        @if (IsSelectionMode) {
          <div class="list-col checkbox-col">
            <input
              type="checkbox"
              [checked]="SelectedCount === FilteredDashboards.length && FilteredDashboards.length > 0"
              [indeterminate]="SelectedCount > 0 && SelectedCount < FilteredDashboards.length"
              (change)="SelectedCount === FilteredDashboards.length ? ClearSelection() : SelectAll()">
          </div>
        }
        <div class="list-col name-col">Name</div>
        <div class="list-col type-col">Type</div>
        <div class="list-col date-col">Modified</div>
        <div class="list-col actions-col">Actions</div>
      </div>
      <!-- Category Rows -->
      @for (category of FilteredChildCategories; track TrackByCategory($index, category)) {
        <div
          class="list-row category-row"
          [class.drop-target]="DragOverChildCategoryId === category.ID"
          (click)="NavigateToCategory(category.ID)"
          (dragover)="OnDragOverChildCategory(category.ID, $event)"
          (dragleave)="OnDragLeaveChildCategory()"
          (drop)="OnDropOnChildCategory(category.ID, $event)">
          @if (IsSelectionMode) {
            <div class="list-col checkbox-col">
              <!-- Empty placeholder for alignment -->
            </div>
          }
          <div class="list-col name-col">
            <i class="fa-solid fa-folder row-icon folder-icon"></i>
            <span class="dashboard-name" [innerHTML]="HighlightMatch(category.Name)"></span>
          </div>
          <div class="list-col type-col">
            Category
          </div>
          <div class="list-col date-col">
            <!-- No date for categories -->
          </div>
          <div class="list-col actions-col" (click)="$event.stopPropagation()">
            <button
              class="action-btn danger"
              title="Delete category"
              (click)="OnDeleteCategory(category, $event)">
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
        </div>
      }
      <!-- Dashboard Rows -->
      @for (dashboard of FilteredDashboards; track TrackByDashboard($index, dashboard)) {
        <div
          class="list-row"
          [class.selected]="IsSelected(dashboard.ID)"
          [class.dragging]="DraggingId === dashboard.ID"
          [draggable]="AllowDragDrop"
          (click)="OnDashboardClick(dashboard, $event)"
          (dblclick)="OnDashboardDoubleClick(dashboard, $event)"
          (dragstart)="OnDragStart(dashboard, $event)"
          (dragend)="OnDragEnd()">
          @if (IsSelectionMode) {
            <div class="list-col checkbox-col" (click)="$event.stopPropagation()">
              <input
                type="checkbox"
                [checked]="IsSelected(dashboard.ID)"
                (change)="ToggleSelection(dashboard.ID)">
            </div>
          }
          <div class="list-col name-col">
            <i class="fa-solid fa-chart-line row-icon"></i>
            <span class="dashboard-name" [innerHTML]="HighlightMatch(dashboard.Name)"></span>
            @if (IsShared(dashboard.ID)) {
              <span class="shared-badge" title="Shared with you">
                <i class="fa-solid fa-share-nodes"></i>
              </span>
            }
          </div>
          <div class="list-col type-col">
            Dashboard
          </div>
          <div class="list-col date-col">
            {{ FormatDate(dashboard.__mj_UpdatedAt) }}
          </div>
          <div class="list-col actions-col" (click)="$event.stopPropagation()">
            @if (CanEdit(dashboard.ID)) {
              <button
                class="action-btn"
                title="Edit"
                (click)="OnEditDashboard(dashboard, $event)">
                <i class="fa-solid fa-edit"></i>
              </button>
            }
            @if (CanDelete(dashboard.ID)) {
              <button
                class="action-btn danger"
                title="Delete"
                (click)="OnDeleteDashboard(dashboard, $event)">
                <i class="fa-solid fa-trash"></i>
              </button>
            }
          </div>
        </div>
      }
    </div>
  }

  <!-- Empty State -->
  @if (FilteredDashboards.length === 0 && FilteredChildCategories.length === 0 && !IsLoading) {
    <!-- First-time user experience (no dashboards at all) -->
    @if (IsAtRoot && Dashboards.length === 0) {
      <mj-empty-state
        class="dashboard-empty"
        Size="large"
        Icon="fa-solid fa-gauge-high"
        Title="Welcome to Dashboards"
        Message="Create custom dashboards to visualize your data, track key metrics, and monitor important information all in one place."
        [ActionText]="ShowCreateButton ? 'Create Your First Dashboard' : ''"
        ActionIcon="fa-solid fa-plus"
        (Action)="OnCreateDashboard()">
        <div class="empty-features">
          <div class="feature-item">
            <i class="fa-solid fa-table-cells"></i>
            <span>Customizable layouts</span>
          </div>
          <div class="feature-item">
            <i class="fa-solid fa-database"></i>
            <span>Connect to your data</span>
          </div>
          <div class="feature-item">
            <i class="fa-solid fa-share-nodes"></i>
            <span>Share with your team</span>
          </div>
        </div>
      </mj-empty-state>
    }
    <!-- Folder is empty -->
    @if (!IsAtRoot && !SearchText) {
      <mj-empty-state
        class="dashboard-empty"
        Variant="warning"
        Icon="fa-solid fa-folder-open"
        Title="This folder is empty"
        Message="Add dashboards or create sub-folders to organize your content."
        [ActionText]="ShowCreateButton ? 'Create Dashboard' : ''"
        ActionIcon="fa-solid fa-plus"
        (Action)="OnCreateDashboard()" />
    }
    <!-- Root has dashboards but none uncategorized -->
    @if (IsAtRoot && Dashboards.length > 0 && !SearchText) {
      <mj-empty-state
        class="dashboard-empty"
        Icon="fa-solid fa-folder-tree"
        Title="No uncategorized dashboards"
        Message="All your dashboards are organized into folders. Browse the folders above or create a new dashboard here."
        [ActionText]="ShowCreateButton ? 'Create Dashboard' : ''"
        ActionIcon="fa-solid fa-plus"
        (Action)="OnCreateDashboard()" />
    }
    <!-- Search returned no results -->
    @if (SearchText) {
      <mj-empty-state
        class="dashboard-empty"
        Variant="no-results"
        Icon="fa-solid fa-magnifying-glass"
        Title="No results found"
        [Message]="NoResultsMessage"
        ActionText="Clear Search"
        ActionIcon="fa-solid fa-times"
        ActionVariant="secondary"
        (Action)="ClearSearch()" />
    }
  }

  <!-- Category Drop Zones (for drag and drop) -->
  @if (DraggingId && Categories.length > 0) {
    <div class="category-drop-zones">
      <div class="drop-zone-header">Drop to move to category:</div>
      <div class="drop-zones">
        <div
          class="drop-zone"
          [class.active]="DropTargetCategoryId === null"
          (dragover)="OnDragOverCategory(null, $event)"
          (dragleave)="OnDragLeaveCategory()"
          (drop)="OnDropOnCategory(null, $event)">
          <i class="fa-solid fa-folder-open"></i>
          Uncategorized
        </div>
        @for (cat of Categories; track TrackByCategory($index, cat)) {
          <div
            class="drop-zone"
            [class.active]="DropTargetCategoryId === cat.ID"
            (dragover)="OnDragOverCategory(cat.ID, $event)"
            (dragleave)="OnDragLeaveCategory()"
            (drop)="OnDropOnCategory(cat.ID, $event)">
            <i class="fa-solid fa-folder"></i>
            {{ cat.Name }}
          </div>
        }
      </div>
    </div>
  }

  <!-- Delete Confirmation Dialog -->
  <mj-confirm-dialog
    [visible]="ShowDeleteConfirm"
    type="danger"
    title="Delete Dashboard{{ DashboardsPendingDelete.length > 1 ? 's' : '' }}"
        [message]="DashboardsPendingDelete.length === 1
            ? 'Are you sure you want to delete \'' + DashboardsPendingDelete[0].Name + '\'? This action cannot be undone.'
            : 'Are you sure you want to delete ' + DashboardsPendingDelete.length + ' dashboards? This action cannot be undone.'"
    confirmText="Delete"
    cancelText="Cancel"
    (confirmed)="ConfirmDelete()"
    (cancelled)="CloseDeleteConfirm()">
  </mj-confirm-dialog>

  <!-- Move to Folder Dialog -->
  @if (ShowMoveDialog) {
    <div class="move-dialog-overlay" (click)="CloseMoveDialog()">
      <div class="move-dialog" (click)="$event.stopPropagation()">
        <div class="dialog-header">
          <h3>Move to Category</h3>
          <button class="close-btn" (click)="CloseMoveDialog()">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="dialog-content">
          <p>Move {{ SelectedCount }} dashboard{{ SelectedCount > 1 ? 's' : '' }} to:</p>
          <div class="category-list">
            <div
              class="category-option"
              (click)="ConfirmMove(null)">
              <i class="fa-solid fa-folder-open"></i>
              Uncategorized
            </div>
            @for (cat of Categories; track TrackByCategory($index, cat)) {
              <div
                class="category-option"
                (click)="ConfirmMove(cat.ID)">
                <i class="fa-solid fa-folder"></i>
                {{ cat.Name }}
              </div>
            }
          </div>
        </div>
        <div class="dialog-footer">
          <button class="btn-secondary" (click)="CloseMoveDialog()">Cancel</button>
        </div>
      </div>
    </div>
  }

  <!-- Create Category Dialog -->
  @if (ShowCreateCategoryDialog) {
    <div class="create-category-dialog-overlay" (click)="CloseCreateCategoryDialog()">
      <div class="create-category-dialog" (click)="$event.stopPropagation()">
        <div class="dialog-header">
          <h3>
            <i class="fa-solid fa-folder-plus"></i>
            Create Category
          </h3>
          <button class="close-btn" (click)="CloseCreateCategoryDialog()">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="dialog-content">
          <div class="form-field">
            <label for="category-name">Name *</label>
            <input
              type="text"
              id="category-name"
              [(ngModel)]="NewCategoryName"
              placeholder="Enter category name"
              (keyup.enter)="ConfirmCreateCategory()">
          </div>
          <div class="form-field">
            <label for="category-description">Description</label>
            <textarea
              id="category-description"
              [(ngModel)]="NewCategoryDescription"
              placeholder="Optional description"
            rows="3"></textarea>
          </div>
        </div>
        <div class="dialog-footer">
          <button
            class="btn-primary"
            (click)="ConfirmCreateCategory()"
            [disabled]="!NewCategoryName.trim()">
            <i class="fa-solid fa-plus"></i>
            Create
          </button>
          <button class="btn-secondary" (click)="CloseCreateCategoryDialog()">Cancel</button>
        </div>
      </div>
    </div>
  }

  <!-- Delete Category Confirmation Dialog -->
  @if (ShowDeleteCategoryConfirm) {
    <div class="delete-category-dialog-overlay" (click)="CloseDeleteCategoryConfirm()">
      <div class="delete-category-dialog" (click)="$event.stopPropagation()">
        <div class="dialog-header danger">
          <h3>
            <i class="fa-solid fa-exclamation-triangle"></i>
            Delete Category
          </h3>
          <button class="close-btn" (click)="CloseDeleteCategoryConfirm()">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        @if (CategoryPendingDelete) {
          <div class="dialog-content">
            <div class="warning-message">
              <p>
                You are about to delete the category <strong>"{{ CategoryPendingDelete.Name }}"</strong>.
              </p>
              @if (GetCategoryContentCount(CategoryPendingDelete); as counts) {
                @if (counts.dashboards > 0 || counts.categories > 0) {
                  <p class="content-warning">
                    <i class="fa-solid fa-warning"></i>
                    This category contains:
                  </p>
                }
                @if (counts.dashboards > 0 || counts.categories > 0) {
                  <ul class="content-list">
                    @if (counts.dashboards > 0) {
                      <li>
                        <i class="fa-solid fa-chart-line"></i>
                        {{ counts.dashboards }} dashboard{{ counts.dashboards !== 1 ? 's' : '' }}
                      </li>
                    }
                    @if (counts.categories > 0) {
                      <li>
                        <i class="fa-solid fa-folder"></i>
                        {{ counts.categories }} sub-categor{{ counts.categories !== 1 ? 'ies' : 'y' }}
                      </li>
                    }
                  </ul>
                }
              }
            </div>
            <div class="confirmation-checkbox">
              <label class="checkbox-label">
                <input
                  type="checkbox"
                  [(ngModel)]="DeleteCategoryIncludeContents">
                <span class="checkbox-text">
                  I understand this will permanently delete this category and all its contents. This action cannot be undone.
                </span>
              </label>
            </div>
          </div>
        }
        <div class="dialog-footer">
          <button
            class="btn-danger"
            (click)="ConfirmDeleteCategory()"
            [disabled]="!DeleteCategoryIncludeContents">
            <i class="fa-solid fa-trash"></i>
            Delete Category
          </button>
          <button class="btn-secondary" (click)="CloseDeleteCategoryConfirm()">Cancel</button>
        </div>
      </div>
    </div>
  }
</div>
